import { lazy } from 'react'
import { redirect } from 'react-router-dom'

import {
  ContributorNameDocument,
  ContributorSelectorAllDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { createGraphqlDataSourceDefinition } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { createBreadcrumbSelectDefinition } from 'modules/core/lib/navigation'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import ContributorHeader from 'modules/project_management/components/headers/ContributorHeader'

const ContributorsRootPage = lazy(
  () => import('modules/project_management/pages/ContributorsRootPage')
)
const ContributorsListPage = lazy(
  () => import('modules/project_management/pages/ContributorsListPage')
)
const ContributorViewPage = lazy(
  () => import('modules/project_management/pages/ContributorsListPage/ContributorViewPage')
)

const MyProjectsOverviewPage = lazy(
  () => import('modules/project_management/pages/MyProjectsPage/OverviewPage')
)
const MyProjects = lazy(() => import('modules/project_management/pages/MyProjectsPage/MyProjects'))

const MyProjectsSchedulePage = lazy(
  () => import('modules/project_management/pages/MyProjectsPage/SchedulePage')
)

export const contributorsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.projects.contributors.path,
      element: <ContributorsRootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('project_management.meta_data.contributors.title'),
          description: i18n.t('project_management.meta_data.contributors.description'),
          illustration: 'group',
        }),
      },
      children: [
        {
          index: true,
          loader: () => redirect(navigation.projects.contributors.list.path),
        },
        {
          path: navigation.projects.contributors.list.path,
          element: <ContributorsListPage />,
        },
        {
          path: navigation.projects.contributors.contributorId.path,
          element: <ContributorViewPage />,
          handle: {
            resource: true,
            meta: ({ params, watchQuery }) =>
              watchQuery({
                query: ContributorNameDocument,
                fetchPolicy: 'cache-first',
                variables: {
                  id: Number(params['employeeId']),
                },
              }).map(({ data }) => {
                const contributor = data.projectManagement.currentContributor.nodes[0]
                const employee = contributor?.employee
                const rate = contributor?.rates[0]
                if (!employee) {
                  return { title: '' }
                }
                const avatarURL = buildUrl(employee.avatar?.url)
                const jobTitle =
                  (employee.referenceContract?.jobCatalogTreeLevel?.ancestor?.name || '') +
                  ' ' +
                  (employee.referenceContract?.jobCatalogTreeLevel?.name || '')

                const costRate =
                  rate?.chargingCostCents ?? contributor?.defaultRateCost?.chargingCostCents ?? 0

                const previousEmployee = data.projectManagement.previousEmployee.nodes[0]
                const nextEmployee = data.projectManagement.nextEmployee.nodes[0]

                const now = moment()
                const year = now.startOf('week').format('YYYY')
                const month = now.startOf('week').format('MM')
                const day = now.startOf('week').format('DD')

                const canManageRate = !!data.projectManagement._policies.rateManage.can

                const source = createGraphqlDataSourceDefinition({
                  document: ContributorSelectorAllDocument,
                  mapVariables: ({ search }) => {
                    return {
                      ids: [],
                      employeeName: search ?? '',
                    }
                  },
                  mapData: (queryData) => {
                    return queryData.projectManagement.projectContributorsConnection.nodes
                  },
                  mapPagination: (queryData) => {
                    const pageInfo =
                      queryData.projectManagement.projectContributorsConnection.pageInfo
                    return {
                      total: 0,
                      cursor: pageInfo.endCursor ?? '0',
                      hasNextPage: pageInfo.hasNextPage ?? false,
                    }
                  },
                  paginationType: 'infinite-scroll',
                })

                const breadcrumbSelect = createBreadcrumbSelectDefinition<
                  ReturnType<typeof source.mapData>[number],
                  typeof source
                >({
                  url: (value: string) =>
                    navigation.projects.contributors.contributorId.overview.fullPath({
                      employeeId: value,
                    }),
                  mapOptions: (contributorItem) => {
                    return {
                      label:
                        contributorItem.employee?.fullName ||
                        `${contributorItem.employee?.firstName ?? ''} ${
                          contributorItem.employee?.lastName ?? ''
                        }`.trim(),
                      value: contributorItem.id.toString(),
                      avatar: {
                        type: 'person' as const,
                        firstName:
                          contributorItem.employee?.preferredName ||
                          contributorItem.employee?.firstName ||
                          '',
                        lastName: contributorItem.employee?.lastName || '',
                        src: buildUrl(contributorItem.employee?.avatar?.url) || undefined,
                      },
                    }
                  },
                  source,
                  defaultItem: {
                    label: `${employee.firstName} ${employee.lastName}`,
                    value: params['employeeId'] ?? '',
                    avatar: {
                      type: 'person' as const,
                      firstName: employee.firstName || '',
                      lastName: employee.lastName || '',
                      src: buildUrl(employee.avatar?.url) || undefined,
                    },
                  },
                })

                return {
                  title: `${employee.firstName} ${employee.lastName}`,
                  headerComponentProps: !canManageRate
                    ? {
                        title: `${employee.firstName} ${employee.lastName}`,
                        avatar: {
                          src: buildUrl(employee.avatar?.url) || undefined,
                          firstName: employee.firstName,
                          lastName: employee.lastName,
                          type: 'person',
                        },
                        description: jobTitle,
                      }
                    : undefined,
                  headerComponent: canManageRate ? (
                    <ContributorHeader
                      avatarURL={avatarURL}
                      billRate={rate?.billingRateCents ?? 0}
                      canManageRate={canManageRate}
                      currency={rate?.currency ?? employee.legalEntity?.currency ?? ''}
                      employeeName={employee.firstName}
                      employeeLastName={employee.lastName}
                      costRate={costRate}
                      profitMargin={rate?.profitabilityPercentageCents ?? 0}
                      employeeId={employee.id}
                      jobTitle={jobTitle}
                    />
                  ) : undefined,
                  siblingsNavigation: {
                    previous: previousEmployee
                      ? {
                          title: previousEmployee.employee?.fullName,
                          url: navigation.projects.contributors.contributorId.overview.fullPath({
                            employeeId: previousEmployee.id,
                          }),
                        }
                      : undefined,
                    next: nextEmployee
                      ? {
                          title: nextEmployee.employee?.fullName,
                          url: navigation.projects.contributors.contributorId.overview.fullPath({
                            employeeId: nextEmployee.id,
                          }),
                        }
                      : undefined,
                    select: breadcrumbSelect,
                  },
                  subNavigation: [
                    {
                      href: navigation.projects.contributors.contributorId.overview.fullPath({
                        employeeId: employee.id,
                      }),
                      title: i18n.t('project_management.my_projects.tabs.overview'),
                      index: true,
                    },
                    {
                      href: navigation.projects.contributors.contributorId.yearMonthDay.fullPath({
                        employeeId: employee.id,
                        year,
                        month,
                        day,
                      }),
                      title: i18n.t('project_management.my_projects.tabs.tracking'),
                    },
                    {
                      href: navigation.projects.contributors.contributorId.schedule.fullPath({
                        employeeId: employee.id,
                      }),
                      title: i18n.t('project_management.my_projects.tabs.schedule'),
                    },
                  ],
                }
              }),
          },
          children: [
            {
              index: true,
              loader: () => redirect(navigation.projects.contributors.contributorId.overview.path),
            },
            {
              path: navigation.projects.contributors.contributorId.overview.path,
              element: <MyProjectsOverviewPage />,
            },
            {
              path: navigation.projects.contributors.contributorId.yearMonthDay.path,
              element: <MyProjects />,
            },
            {
              path: navigation.projects.contributors.contributorId.schedule.path,
              element: <MyProjectsSchedulePage />,
            },
          ],
        },
      ],
    },
  ]
}
